export const NOTES_PAGE_REQUEST_ERROR = Object.freeze({
    OFFLINE: 'offline',
    CLIENT: 'client',
    RELOAD: 'reload',
    RETRYABLE: 'retryable',
});

const LOCAL_NOTES_PAGE_REQUEST_ERROR = null; // Set to a NOTES_PAGE_REQUEST_ERROR value to simulate it locally.

export function normalizeNotesQuery(value) {
    // Matches the default PHP mb_trim() character set used by resolveNotesQuery().
    return String(value ?? '').replace(
        /^[\0\t-\r \x85\u00a0\u1680\u180e\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]+|[\0\t-\r \x85\u00a0\u1680\u180e\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]+$/g,
        '',
    );
}

export function buildNotesSearchPageUrl(initialPageUrl, query) {
    const url = new URL(initialPageUrl.href);
    url.searchParams.set('notes_q', query);

    return url.pathname + url.search;
}

let notesPageRequestCountTest = 0;

export async function requestNotesPage({
    fetchImpl,
    url,
    signal,
    baseUrl,
    initialPageUrl,
    expectedQuery,
}) {
    // ====== my test =====
    if (LOCAL_NOTES_PAGE_REQUEST_ERROR !== null && notesPageRequestCountTest > 0) {
        console.log(notesPageRequestCountTest);
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve({ error: LOCAL_NOTES_PAGE_REQUEST_ERROR });
            }, 3000);
        });
    }
    notesPageRequestCountTest++;
    // ====== my test =====

    let response;
    try {
        response = await fetchImpl(url, {
            method: 'GET',
            headers: { Accept: 'text/html' },
            credentials: 'same-origin',
            signal,
        });
    } catch (_error) {
        if (signal.aborted) {
            return { aborted: true };
        }

        return {
            error: globalThis.navigator?.onLine === false
                ? NOTES_PAGE_REQUEST_ERROR.OFFLINE
                : NOTES_PAGE_REQUEST_ERROR.RETRYABLE,
        };
    }

    if (response.redirected === true) {
        return { error: NOTES_PAGE_REQUEST_ERROR.RELOAD };
    }
    if (response.status >= 500) {
        return { error: NOTES_PAGE_REQUEST_ERROR.RETRYABLE };
    }
    if (!response.ok) {
        return {
            error: response.status >= 400 && response.status < 500
                ? NOTES_PAGE_REQUEST_ERROR.CLIENT
                : NOTES_PAGE_REQUEST_ERROR.RELOAD,
        };
    }

    let html;
    try {
        html = await response.text();
    } catch (_error) {
        if (signal.aborted) {
            return { aborted: true };
        }

        return {
            error: globalThis.navigator?.onLine === false
                ? NOTES_PAGE_REQUEST_ERROR.OFFLINE
                : NOTES_PAGE_REQUEST_ERROR.RETRYABLE,
        };
    }

    try {
        const page = parseNotesPage(html, url, baseUrl, initialPageUrl, expectedQuery);

        return page === null ? { error: NOTES_PAGE_REQUEST_ERROR.RELOAD } : { page };
    } catch (_error) {
        return { error: NOTES_PAGE_REQUEST_ERROR.RELOAD };
    }
}

function parseNotesPage(html, requestUrl, baseUrl, initialPageUrl, expectedQuery) {
    const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
    const chunk = parsedDocument.querySelector('[data-notes-chunk]');
    const totalCount = chunk instanceof HTMLElement ? String(chunk.dataset.totalCount ?? '') : '';
    const matchedCount = chunk instanceof HTMLElement ? String(chunk.dataset.matchedCount ?? '') : '';
    if (
        !(chunk instanceof HTMLElement)
        || !/^(0|[1-9]\d*)$/.test(totalCount)
        || !/^(0|[1-9]\d*)$/.test(matchedCount)
    ) {
        return null;
    }

    const rawNextPageUrl = String(chunk.dataset.nextPageUrl ?? '').trim();
    if (rawNextPageUrl !== '') {
        let requested;
        let next;
        try {
            requested = new URL(requestUrl, baseUrl);
            next = new URL(rawNextPageUrl, baseUrl);
        } catch (_error) {
            return null;
        }
        if (
            next.origin !== initialPageUrl.origin
            || next.pathname !== initialPageUrl.pathname
            || String(next.searchParams.get('notes_cursor') ?? '').trim() === ''
            || (expectedQuery === ''
                ? next.searchParams.has('notes_q')
                : next.searchParams.get('notes_q') !== expectedQuery)
            || next.hash !== ''
            || next.href === requested.href
        ) {
            return null;
        }
    }

    const noteList = chunk.querySelector('.game-note-list');
    if (rawNextPageUrl !== '' && !(noteList instanceof HTMLElement)) {
        return null;
    }

    return {
        chunk,
        noteList,
        totalCount,
        matchedCount,
        nextPageUrl: rawNextPageUrl,
        warning: chunk.querySelector('.game-notes-inline-warning'),
    };
}
